import { useRef, useState } from 'react';
import { useReward } from '@/contexts/reward-context';
import { replaceResource, getProgressPercentage, addCommas } from '@/utils/site';
import { Tooltip } from './tooltip';
import { RewardScene, RewardType, Event, EventCardProps } from '@/types';
import { EventLayout } from '@/layouts/event-layout';
import { ArrowDownIcon, ArrowRightIcon, ArrowUpIcon, InfoIcon, LockIcon } from './svg-icons';

const EventCard = ({ data, rewardType, rewardId }: EventCardProps) => {
	const { labels, goToEvent, onKafkaSet } = useReward();

	const onClick = () => {
		let action = 1;
		switch (rewardId) {
			case 1:
				action = 27;
				break;
			case 2:
				action = 28;
				break;
			case 3:
				action = 29;
				break;
			case 6:
				action = 25;
				break;
			case 7:
				action = 26;
				break;
		}
		
		onKafkaSet({
			type: 2,
			action: action
		});

		if (goToEvent) {
			goToEvent(rewardType as number, rewardId as number, false);
		}
	}

	return (
		<div className="reward-chlg-item">
			{
				rewardType === RewardType.BookedBet ? (
					<div className="reward-coin">
						<div className="reward-coin__icon">
							<i className="reward-iconcolor-shop-coin"></i>
						</div>
						<div className="reward-coin__text">100</div>
					</div>
				) : (
					<div className="reward-exp-point">
						<div className="reward-exp-point__icon">
							<i className="reward-iconcolor-exp-star" />
						</div>
						<div className="reward-exp-point__text">{data.Exp}</div>
					</div>
				)
			}
			<div className="reward-chlg-item__info">
				<div className="reward-chlg-item__title">
					<span className="reward-chlg-item__name">{replaceResource(labels?.[data.Description] ?? '', addCommas(data.Threshold))}</span>
				</div>
				<div className="reward-progress">
					<div className="reward-progress__meter">
						<div 
							className="reward-progress__bar" 
							style={{
								width: `${getProgressPercentage(data.Progress, data.Threshold)}%`
							}}
						/>
					</div>
				</div>
			</div>
			{
				!data.CompleteDate ?
					(
						data.IsCurrentLevel ?
							<button 
								className="reward-btn reward-btn--primary" 
								onClick={onClick}
								type="button"
							>
								<div className="reward-btn__info">
									<span className="reward-btn__text">{labels?.lbl_RPGo}</span>
								</div>
								<ArrowRightIcon />
							</button> :
							<button className="reward-btn reward-btn--lock" type="button">
								<LockIcon />
							</button>
					) : null
			}
		</div>
	);
};

export const EventList = () => {
	const { labels, eventListReward, platform, onKafkaSet } = useReward();
	const [isShowingDetail, setIsShowingDetail] = useState(false);
	const challengeTitleRef = useRef<HTMLDivElement>(null);
	const [isShowingCompleted, setIsShowingCompleted] = useState((eventListReward?.Events || []).every((event: Event): boolean => !!event.CompleteDate));

	if (!eventListReward) {
		return null;
	}

	const toggleToolTip = () => {
		let action = 1;
		switch (eventListReward.RewardId) {
			case 1:
				action = 22;
				break;
			case 2:
				action = 23;
				break;
			case 3:
				action = 24;
				break;
			case 6:
				action = 20;
				break;
			case 7:
				action = 21;
				break;
		}
		
		onKafkaSet({
			type: 2,
			action: action
		});
		setIsShowingDetail((prev) => !prev);
	}

	const toggleShowingCompleted = () => {
		const isShowComplete = !isShowingCompleted
		
		const kafkaAction = {
			[RewardType.Betting]: 22,
			[RewardType.Parlay]: 23,
			[RewardType.Streamer]: 24,
		}[eventListReward.RewardType];

		if (onKafkaSet && isShowComplete) {
			onKafkaSet({
				type: 2,
				action: kafkaAction
			})
		}

		setIsShowingCompleted(isShowComplete);
	}

	const { processing, completed }: { [key: string]: Event[] } = (eventListReward.Events || []).reduce((result: { [key: string]: Event[] }, event: Event): { [key: string]: Event[] } => {
		result[!event.CompleteDate ? 'processing' : 'completed'].push(event);
		return result;
	}, { processing: [], completed: [] });

	const resourceToHtml = () => {
		const resourceArr = eventListReward?.RewardFullDescription?.split(',');

		const children = resourceArr?.reduce((children, resource) => {
			children.push(
				<div
					key={resource}
					className="reward-tooltip__text"
				>
					{labels?.[resource]}
				</div>
			);

			return children;
		}, new Array<React.ReactNode>);

		return children;
	}

	return (
		<EventLayout scene={RewardScene.EventList}>
			<div className="reward-chlg-detail">
				<div className="reward-chlg-detail__info">
					<i className={{
						[RewardType.Betting]: 'reward-iconcolor-img-betting',
						[RewardType.Parlay]: 'reward-iconcolor-img-parlay',
						[RewardType.Streamer]: 'reward-iconcolor-img-streamer',
						[RewardType.Survey]: 'reward-iconcolor-img-survey',
						[RewardType.BookedBet]: 'reward-iconcolor-img-fast'
					}[eventListReward.RewardType]}
					/>
					<div 
						ref={challengeTitleRef}
						className="reward-chlg-detail__title" 
						onClick={toggleToolTip}
						data-show={isShowingDetail}
					>
						<span className="reward-chlg-detail-text">{labels?.[eventListReward.RewardName]}</span>
						<InfoIcon />
						<Tooltip
							isShowingDetail={isShowingDetail}
							setIsShowingDetail={setIsShowingDetail}
							rewardName={eventListReward.RewardName}
							triggerRef={challengeTitleRef}
						>
							{resourceToHtml()}
						</Tooltip>
					</div>
					<div className="reward-chlg-detail__subtitle">
						{labels?.[eventListReward.RewardDescription]}
					</div>
				</div>
				{
					processing.length > 0 && (
						<>
							<div className="reward-chlg-head">
								<i className="reward-iconcolor-undone" />
								<div className="reward-chlg-head__title">
									{labels?.lbl_RPInProgress}
								</div>
							</div>
							<div className="reward-chlg-list">
								{
									processing.map(event => {
										return (
											<EventCard
												key={event.Level}
												data={event}
												rewardId={eventListReward.RewardId}
												rewardType={eventListReward.RewardType}
											/>
										)
									})
								}
							</div>
						</>
					)
				}
				{
					completed.length > 0 && (
						<>
							<div
								className="reward-chlg-head"
								onClick={toggleShowingCompleted}
								data-show={isShowingCompleted}
							>
								<i className="reward-iconcolor-prize" />
								<div className="reward-chlg-head__title">
									{labels?.lbl_RPCompleted}
								</div>
								{
									isShowingCompleted ?
										<ArrowUpIcon /> :
										<ArrowDownIcon />
								}
							</div>
							<div className="reward-chlg-list" data-show={isShowingCompleted}>
								{
									completed.map(event => {
										return (
											<EventCard
												key={event.Level}
												data={event}
												rewardId={eventListReward.RewardId}
												rewardType={eventListReward.RewardType}
											/>
										)
									})
								}
							</div>
						</>
					)
				}
			</div>
		</EventLayout>
	)
}
